import { ref } from 'vue'

import { useAppStore } from '@/stores/app'
import type { IAllowedInstanceType } from '@/types/Auth'

// The instance types the signed-in user may start a workspace at. A plane that predates the
// feature does not carry the field at all, which reads here as "no choice" — and then
// nothing about instance types is ever shown.
export const allowedInstanceTypes = (): IAllowedInstanceType[] =>
    useAppStore().currentUser?.allowed_instance_types ?? []

// One type is not a choice: the start goes out without asking, no card carries a label, and
// no menu offers a change. This is the whole rule (the contract's `allowed_instance_types`).
export const hasInstanceTypeChoice = (): boolean => allowedInstanceTypes().length > 1

export const defaultAllowedInstanceType = (): IAllowedInstanceType | null => {
    const types = allowedInstanceTypes()

    return types.find(type => type.is_default) ?? types[0] ?? null
}

// What a type gives you is drawn rather than written — `components/InstanceTypeSummary.vue`
// puts a chip, a memory stick and a disk in front of the three figures.

// Whether a type is at its cap already. `-1` is unlimited and never reached.
export const instanceTypeAtCap = (type: IAllowedInstanceType): boolean =>
    type.max_running !== -1 && type.running >= type.max_running

// Whether this host can give a workspace hardware virtualisation. A plane that predates the
// field reads as "no", which is the safe direction: a size that needs it is greyed out with
// a reason rather than offered and then refused with error code 15.
export const kvmAvailable = (): boolean => useAppStore().currentUser?.kvm_available ?? false

// Whether a size cannot be started on this host at all — it asks for virtualisation the
// machine has not got. It is the picker's second reason to grey a card out, next to the cap,
// and it is the same refusal the plane makes (invariant 28).
export const instanceTypeNeedsKVM = (type: IAllowedInstanceType): boolean =>
    type.kvm && !kvmAvailable()

// ---------------------------------------------------------------------------------------
// The remembered choice, per project
// ---------------------------------------------------------------------------------------
//
// Somebody who starts one project on the big size and another on the small one should not
// have to say so twice, so the last pick is kept per project. It is a convenience and never
// a source of truth: a stored id that is no longer allowed is ignored, and a browser that
// refuses storage simply forgets.

const storageKey = (projectGitLabId: number): string => `instance_type:${projectGitLabId}`

export const rememberedInstanceType = (projectGitLabId: number): string => {
    try {
        const stored = localStorage.getItem(storageKey(projectGitLabId)) ?? ''

        return allowedInstanceTypes().some(type => type.id === stored) ? stored : ''
    } catch {
        return ''
    }
}

export const rememberInstanceType = (projectGitLabId: number, id: string) => {
    try {
        if (id) localStorage.setItem(storageKey(projectGitLabId), id)
    } catch {
        // A browser that will not store this loses the convenience and nothing else.
    }
}

// What the picker opens on: what this project was last started with, then what the
// environment runs now, then the user's default, then whatever is first.
export const preselectedInstanceType = (projectGitLabId: number, currentId = ''): string => {
    const remembered = rememberedInstanceType(projectGitLabId)
    if (remembered) return remembered

    if (currentId && allowedInstanceTypes().some(type => type.id === currentId)) return currentId

    return defaultAllowedInstanceType()?.id ?? ''
}

// ---------------------------------------------------------------------------------------
// The picker dialog
// ---------------------------------------------------------------------------------------
//
// A start is written in libs/environment.ts, which has no template of its own, so the one
// dialog lives in the app shell and is opened through this promise. Cancelling resolves to
// null, and a start that was cancelled is simply not sent.

export interface InstanceTypePickOptions {
    // Which project the choice is remembered against.
    projectGitLabId: number
    // The type the environment runs at right now, when there is one.
    currentId?:      string
    // What the confirm button says: Start for a start, Save for a change.
    confirmLabel:    string
    confirmIcon?:    string
    // Whether the dialog also asks which Claude account to start under. A start where both
    // questions apply is ONE dialog with two sections rather than two dialogs in a row; the
    // change-the-size flow never asks it.
    withAccount?:    boolean
}

// What the dialog answers with. `accountId` is an empty string whenever the account section
// was not shown, which is exactly what a start body treats as "say nothing about it".
export interface InstanceTypePick {
    typeId:    string
    accountId: string
}

export const instanceTypePickerOpen    = ref(false)
export const instanceTypePickerOptions = ref<InstanceTypePickOptions>({
    projectGitLabId: 0,
    confirmLabel:    '',
})

let resolver: ((pick: InstanceTypePick | null) => void) | null = null

export const resolveInstanceTypePick = (pick: InstanceTypePick | null) => {
    instanceTypePickerOpen.value = false

    if (!resolver) return

    const resolve = resolver
    resolver = null
    resolve(pick)
}

export const pickInstanceType = (options: InstanceTypePickOptions): Promise<InstanceTypePick | null> => {
    // A second opening would orphan the first promise, so it is answered first.
    resolveInstanceTypePick(null)

    instanceTypePickerOptions.value = options
    instanceTypePickerOpen.value    = true

    return new Promise<InstanceTypePick | null>((resolve) => { resolver = resolve })
}
